import { Link } from "react-router-dom";
import { Instagram, Facebook, Linkedin, Phone } from "lucide-react";
import Marquee from "./Marquee";

const socials = [
  {
    label: "Instagram",
    href: "https://www.instagram.com/njia_safaris/",
    icon: Instagram,
    handle: "@njia_safaris",
  },
  {
    label: "Facebook",
    href: "https://www.facebook.com/profile.php?id=61591948211608",
    icon: Facebook,
    handle: "Njia Safaris",
  },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/in/praveen-nagpal-8a5429407/",
    icon: Linkedin,
    handle: "Praveen Nagpal",
  },
  {
    label: "X",
    href: "https://x.com/njaisafaris",
    // X (Twitter) inline glyph — lucide's Twitter icon is now legacy
    icon: (props) => (
      <svg
        viewBox="0 0 24 24"
        width={props.size || 20}
        height={props.size || 20}
        stroke="currentColor"
        strokeWidth={props.strokeWidth || 1.4}
        fill="none"
        {...props}
      >
        <path
          d="M18.244 2H21.5l-7.5 8.57L22.5 22h-6.844l-5.36-6.86L4 22H.75l8.02-9.17L.75 2h7.006l4.84 6.24L18.244 2Zm-2.4 18h1.9L7.13 4H5.15l10.694 16Z"
          fill="currentColor"
          stroke="none"
        />
      </svg>
    ),
    handle: "@njaisafaris",
  },
];

const phones = [
  { label: "Praveen · Head office", tel: "+919650595152", display: "+91 96505 95152" },
  { label: "Alt line", tel: "+919711735963", display: "+91 97117 35963" },
];

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="section-dark">
      <Marquee
        items={["Serengeti", "Masai Mara", "Amboseli", "Bwindi", "Ngorongoro", "Laikipia", "Ruaha", "Samburu", "Zanzibar"]}
        className="border-t border-b border-[color:var(--line-invert)] py-8"
      />

      <div className="container-x py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-10">
        {/* Brand */}
        <div className="md:col-span-4">
          <div className="flex items-center gap-3">
            <span
              className="inline-block h-[8px] w-[8px] rotate-45"
              style={{ background: "var(--gold)" }}
            />
            <span className="font-display text-3xl text-[color:var(--ivory)]">
              Njia <span className="italic text-[color:var(--gold-3)]">Safaris</span>
            </span>
          </div>
          <p className="mt-6 max-w-md text-[color:var(--sand)] text-[15px] leading-relaxed">
            Ultra-luxury private safaris across Kenya, Tanzania and Uganda. Every journey planned by hand,
            every detail attended to. Your path to Africa&rsquo;s soul.
          </p>

          {/* Social icons */}
          <div className="mt-8">
            <p className="eyebrow eyebrow-invert mb-4">Follow</p>
            <div className="flex items-center gap-3">
              {socials.map((s) => {
                const Icon = s.icon;
                return (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Njia Safaris on ${s.label}`}
                    data-testid={`social-${s.label.toLowerCase()}`}
                    className="h-10 w-10 flex items-center justify-center border border-[color:var(--line-invert)] text-[color:var(--ivory)] hover:bg-[color:var(--gold)] hover:text-[color:var(--ink)] hover:border-[color:var(--gold)] transition-all duration-500"
                    title={`${s.label} — ${s.handle}`}
                  >
                    <Icon size={16} strokeWidth={1.4} />
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        {/* Journeys */}
        <div className="md:col-span-3">
          <p className="eyebrow eyebrow-invert mb-5">Journeys</p>
          <ul className="space-y-3">
            <li><Link to="/journeys" className="link-under text-[color:var(--ivory)]">All journeys</Link></li>
            <li><Link to="/experiences" className="link-under text-[color:var(--ivory)]">Experiences</Link></li>
            <li><Link to="/signature-journey" className="link-under text-[color:var(--ivory)]">2026 Signature</Link></li>
            <li><Link to="/lens-of-jeet-aman" className="link-under text-[color:var(--ivory)]">The Lens</Link></li>
            <li><Link to="/about" className="link-under text-[color:var(--ivory)]">About</Link></li>
            <li><Link to="/contact" className="link-under text-[color:var(--ivory)]">Begin planning</Link></li>
          </ul>
        </div>

        {/* Correspondence */}
        <div className="md:col-span-5">
          <p className="eyebrow eyebrow-invert mb-5">Correspondence</p>
          <ul className="space-y-3 text-[color:var(--ivory)]">
            <li>
              <a
                href="mailto:praveen.nagpal@njiasafaris.com"
                className="font-display text-2xl break-all hover:text-[color:var(--gold-3)] transition-colors"
                data-testid="footer-email"
              >
                praveen.nagpal@njiasafaris.com
              </a>
            </li>
            {phones.map((p) => (
              <li key={p.tel} className="flex items-center gap-3">
                <Phone size={14} strokeWidth={1.4} className="text-[color:var(--gold-3)] shrink-0" />
                <a
                  href={`tel:${p.tel}`}
                  className="font-display text-xl md:text-2xl hover:text-[color:var(--gold-3)] transition-colors"
                  data-testid={`footer-phone-${p.tel}`}
                >
                  {p.display}
                </a>
                <span className="mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--sand)]/60">
                  {p.label}
                </span>
              </li>
            ))}
            <li className="text-[color:var(--sand)] text-sm mt-2">Noida · India · Head office</li>
            <li className="text-[color:var(--sand)] text-sm">Nairobi &amp; Arusha · East Africa · On the ground</li>
          </ul>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/contact" className="btn-ghost btn-ghost-invert" data-testid="footer-enquire">
              Begin Planning
            </Link>
            <a
              href={`https://wa.me/919650595152?text=${encodeURIComponent("Hello Njia Safaris — I'd like to speak with a specialist.")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mono text-[11px] uppercase tracking-[0.28em] text-[color:var(--gold-3)] link-under"
              data-testid="footer-whatsapp"
            >
              WhatsApp us →
            </a>
          </div>
        </div>
      </div>

      <div className="hairline hairline-invert" />
      <div className="container-x py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
        <p className="mono text-[11px] uppercase tracking-widest text-[color:var(--sand)]">
          © {new Date().getFullYear()} Njia Safaris — All journeys planned in private
        </p>
        <p className="mono text-[11px] uppercase tracking-widest text-[color:var(--sand)]">
          njia — the path
        </p>
      </div>
    </footer>
  );
}
